import type { WebSocketMessageDataAiEvent } from '../../interfaces/WebSocketMessage';

type AiEventFileData = WebSocketMessageDataAiEvent | Record<string, unknown>;

function toPositiveFileRef(value: unknown): number | string | undefined {
  if (value == null || value === '') {
    return undefined;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return undefined;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }

  return undefined;
}

function firstArrayFileRef(values: unknown): number | string | undefined {
  if (!Array.isArray(values)) {
    return undefined;
  }

  for (const entry of values) {
    const ref = toPositiveFileRef(entry);
    if (ref != null) {
      return ref;
    }
  }

  return undefined;
}

/** First usable file ref from ai_event data (snake + camel; skips 0/empty). */
export function resolveAiEventFileRef(data: AiEventFileData): number | string | undefined {
  const record = data as Record<string, unknown>;

  const single = toPositiveFileRef(record.file_id ?? record.fileId ?? record.FileId);
  if (single != null) {
    return single;
  }

  return (
    firstArrayFileRef(record.file_ids) ??
    firstArrayFileRef(record.fileIds) ??
    firstArrayFileRef(record.FileIds)
  );
}

type NotificationFileSource = {
  file_id?: number | string;
  sourcePayload?: Record<string, unknown>;
};

function readSourcePayloadData(
  sourcePayload?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const data = sourcePayload?.data;
  return data != null && typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
}

/** File ref from notification root and/or sourcePayload.data (snake + camel). */
export function resolveNotificationFileRef(
  notification: NotificationFileSource,
): number | string | undefined {
  const rootRef = toPositiveFileRef(notification.file_id);
  if (rootRef != null) {
    return rootRef;
  }

  const payloadData = readSourcePayloadData(notification.sourcePayload);
  if (payloadData != null) {
    return resolveAiEventFileRef(payloadData);
  }

  return undefined;
}

/** Numeric file id for getSpecificFiles enrichment. */
export function coerceNotificationFileId(notification: NotificationFileSource): number | undefined {
  const ref = resolveNotificationFileRef(notification);
  if (ref == null) {
    return undefined;
  }
  const numericId = typeof ref === 'number' ? ref : Number(ref);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : undefined;
}

/** Numeric file ids for ai_event row refresh / batch handlers. */
export function coerceAiEventFileIds(data: AiEventFileData): number[] {
  const record = data as Record<string, unknown>;
  const arrayCandidate = record.file_ids ?? record.fileIds ?? record.FileIds;

  if (Array.isArray(arrayCandidate) && arrayCandidate.length > 0) {
    return arrayCandidate
      .map((entry) => toPositiveFileRef(entry))
      .filter((entry): entry is number | string => entry != null)
      .map((entry) => (typeof entry === 'number' ? entry : Number(entry)))
      .filter((id): id is number => Number.isFinite(id) && id > 0);
  }

  const single = resolveAiEventFileRef(record);
  if (single == null) {
    return [];
  }

  const numericId = typeof single === 'number' ? single : Number(single);
  return Number.isFinite(numericId) && numericId > 0 ? [numericId] : [];
}
